import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { ShowcaseRepository } from '../../domain/repositories/ShowcaseRepository'
import {
  ShowcaseShiftsService,
  ShowcasesService,
  type PostShowcaseRequestAdmin,
  type PutShowcaseRequestAdmin,
  type ShowcaseAdminListedDtoAdmin,
  type ShowcaseShiftDayRequestAdmin
} from '@/backend/market'
import type { ShowcaseMiniEntity } from '../../domain/entities/ShowcaseMiniEntity'
import type { ShowcaseFilterEntity } from '../../domain/entities/ShowcaseFilterEntity'
import { ShowcaseFilterModel } from '../models/ShowcaseFilterModel'
import { ShowcaseMiniModel } from '../models/ShowcaseMiniModel'
import type { ShowcaseEntity } from '../../domain/entities/ShowcaseEntity'
import { ShowcaseModel } from '../models/ShowcaseModel'
import type { ShowcaseBranchRequestEntity } from '../../domain/entities/ShowcaseBranchRequestEntity'
import type { ShowcaseItemEntity } from '../../domain/entities/ShowcaseItemEntity'
import type { ShowcaseOrderRequestEntity } from '../../domain/entities/ShowcaseOrderRequestEntity'
import { getChannelCase } from '@/features/channel/domain/usecases/getChannelUsecase'
import { CreateUpdateShowcaseModel } from '../models/CreateUpdateShowcaseModel'
import type { CreateUpdateShowcaseEntity } from '../../domain/entities/CreateUpdateShowcaseEntity'
import type { BranchMiniEntity } from '@/features/branch/domain/entities/BranchMiniEntity'
import { ItemShowcaseModel } from '../models/ItemShowcaseModel'
import { getBranchesCase } from '@/features/branch/domain/usecases/getBranchUsecase'
import { BranchMiniModel } from '@/features/branch/data/models/BranchMiniModel'
import type { ShiftShowCaseEntity } from '../../domain/entities/ShiftShowCaseEntity'
import { ShowCasePeriodModel } from '../models/ShowCasePeriodModel'
import moment from 'moment'
import type { BranchShowcaseMiniEntity } from '../../domain/entities/BranchShowcaseMiniEntity'
import type { BranchShowcaseFilterEntity } from '../../domain/entities/BranchShowcaseFilterEntity'
import { BranchShowcaseFilterModel } from '../models/BranchShowcaseFilterModel'
import { BranchShowcaseMiniModel } from '../models/BranchShowcaseMiniModel'

export class ShowcaseRepositoryImpl implements ShowcaseRepository {
  async getShowcases(
    filterData: ShowcaseFilterEntity
  ): Promise<PaginateEntity<ShowcaseMiniEntity[]> | { error: true }> {
    const filterDataModel = new ShowcaseFilterModel(filterData)
    try {
      const response = await ShowcasesService.getShowcasesAdmin(filterDataModel)
      const showcaseModels = response.items?.map(
        (item: ShowcaseAdminListedDtoAdmin) => new ShowcaseMiniModel(item as ShowcaseMiniEntity)
      )
      const result = new PaginateEntity<ShowcaseMiniEntity[]>(response, showcaseModels)
      return result
    } catch {
      return { error: true }
    }
  }

  async getShowcase(id: number): Promise<ShowcaseEntity | { error: true }> {
    try {
      const response = await ShowcasesService.getShowcaseAdmin({
        id
      })

      const channelResponse = response.channelId
        ? await getChannelCase(response.channelId)
        : undefined
      const result = {
        ...response,
        channel: channelResponse
      }
      const showcaseModel = new ShowcaseModel(result as ShowcaseEntity)
      return showcaseModel
    } catch {
      return { error: true }
    }
  }

  async createShowcase(
    showcaseData: CreateUpdateShowcaseEntity
  ): Promise<{ success: true } | { error: true }> {
    const createShowcaseModel = new CreateUpdateShowcaseModel(showcaseData)

    try {
      await ShowcasesService.postShowcaseAdmin({
        requestBody: createShowcaseModel as PostShowcaseRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateShowcase(
    id: number,
    showcaseData: CreateUpdateShowcaseEntity
  ): Promise<{ success: true } | { error: true }> {
    const updateShowcaseModel = new CreateUpdateShowcaseModel(showcaseData)
    try {
      await ShowcasesService.putShowcaseAdmin({
        id,
        requestBody: updateShowcaseModel as PutShowcaseRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteShowcase(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ShowcasesService.deleteShowcaseAdmin({ id })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getShowcaseBranches(
    filterData: BranchShowcaseFilterEntity
  ): Promise<PaginateEntity<BranchShowcaseMiniEntity[]> | { error: true }> {
    const filterDataModel = new BranchShowcaseFilterModel(filterData)
    try {
      const response = await ShowcasesService.getBranchesByShowcaseAdmin(filterDataModel)
      const showcaseModels = response.items?.map(
        (item: BranchShowcaseMiniEntity) => new BranchShowcaseMiniModel(item)
      )
      const result = new PaginateEntity<BranchShowcaseMiniEntity[]>(response, showcaseModels)
      return result 
    } catch {
      return { error: true }
    }
  }


  async updateShowcaseBranches(
    showcaseId: number,
    branchesData: ShowcaseBranchRequestEntity[],
    removedBranches: ShowcaseBranchRequestEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
        await ShowcasesService.putShowcaseBranchesAdmin({
        id: showcaseId,
        requestBody: {
          addedBranches: branchesData,
          removedBranches: removedBranches
        }
      })
      return { success: true }
    } catch (error) {
      return { error: true }
    }
  }

 async updateShowcaseBranchOrder(
  showcaseId: number,
  branchId: number,
  newOrder: number
): Promise<{ success: true } | { error: true }> {
  try {
    await ShowcasesService.putShowcaseBranchOrderAdmin({
      id: showcaseId,
      requestBody: {
        branchId: branchId,
        order: newOrder
      }
    })
    return { success: true }
  } catch (error) {
    return { error: true }
  }
}

  async getShowcaseItems(
    showcaseId: number
  ): Promise<{ items: ShowcaseItemEntity[]; branch?: BranchMiniEntity } | { error: true }> {
    let branch: BranchMiniEntity | undefined
    try {
      const response = await ShowcasesService.getItemsByShowcaseAdmin({
        id: showcaseId
      })

      if (!response?.items) {
        return { items: [], branch: undefined }
      }
      if (response.items[0].itemBranchId) {
        const branchResponse = await getBranchesCase(
          {
            id: response.items[0].itemBranchId
          },
          true
        )
        branch = new BranchMiniModel(branchResponse.items[0])
      }
      return { items: response.items.map((item) => new ItemShowcaseModel(item)), branch }
    } catch (error) {
      return { error: true }
    }
  }

  async updateShowcaseItems(
    showcaseId: number,
    itemsData: ShowcaseItemEntity[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      const requestData = {
        items: itemsData.map((item, index) => ({
          order: index + 1,
          itemId: item?.id
        }))
      }
      await ShowcasesService.putShowcaseItemsAdmin({
        id: showcaseId,
        requestBody: requestData
      })
      return { success: true }
    } catch (error) {
      return { error: true }
    }
  }

  async putShowcaseOrder(
    showcaseOrderRequests: ShowcaseOrderRequestEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      await ShowcasesService.putShowcaseOrderAdmin({
        id: showcaseOrderRequests.id!,
        requestBody: {
          order: showcaseOrderRequests.order!
        }
      })

      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getShowCaseShifts(showcaseId: number): Promise<ShiftShowCaseEntity[] | { error: true }> {
    try {
      const response = await ShowcaseShiftsService.getShowcaseShiftsAdmin({
        showcaseId
      })

      return response.showcaseShifts
        ? response.showcaseShifts.map((shift) => {
            return {
              dayOfWeek: shift.dayOfWeek,
              periods: shift.periods?.map((period) => {
                return {
                  startTime: period.startTime
                    ? moment.utc(period.startTime, 'HH:mm:ss').local().format('HH:mm')
                    : undefined,
                  endTime: period.endTime
                    ? moment.utc(period.endTime, 'HH:mm:ss').local().format('HH:mm')
                    : undefined,
                  startTimeDayOfWeek: period.startTimeDayOfWeek,
                  endTimeDayOfWeek: period.endTimeDayOfWeek
                }
              })
            }
          })
        : []
    } catch {
      return { error: true }
    }
  }
  async updateShowCaseShifts(
    showcaseId: number,
    showCaseShiftData: ShiftShowCaseEntity[]
  ): Promise<ShiftShowCaseEntity[] | { error: true }> {
    const shifts = showCaseShiftData.filter((shift) => shift.periods && shift.periods?.length > 0)
    const showCasePeriodModel = shifts.map((shift) => {
      return {
        dayOfWeek: shift.dayOfWeek,
        periods: shift.periods?.map((period) => new ShowCasePeriodModel(period))
      }
    })
    try {
      const response = await ShowcaseShiftsService.putShowcaseShiftsAdmin({
        showcaseId: showcaseId,
        requestBody: {
          showcaseShiftDayRequests: showCasePeriodModel as ShowcaseShiftDayRequestAdmin[]
        }
      })

      return response.showcaseShifts ?? []
    } catch {
      return { error: true }
    }
  }
}
